import * as Sentry from '@sentry/nextjs';
import { logPageClickBi } from './bi-logger';

/**
 * A chat send that never reached the server — and what the composer should do about it.
 *
 * **Why this exists.** A user typed a long message, pressed Send several times, and it never arrived.
 * Every backend log agreed: no request was ever made, so nothing failed and nothing was recorded. The
 * composer clears the field the moment it hands the text to `useChat`, and `send`/`steer` had paths that
 * then returned without a request and without a word — the text was gone and there was no trace of why.
 *
 * So every such path now (a) reports itself here — a Sentry warning tagged with the reason, searchable
 * per account (`lib/sentry-identity.ts` puts the metasite on every event), plus a BI click — and
 * (b) says so in its return value, so the composer can put the text back. Never the text itself: the
 * length is enough to tell a one-liner from a long draft.
 *
 * Reasons are shared 1:1 with iOS/Android `reportSendDropped`; the web-only ones are `busy` (a turn
 * still streaming when `send` ran), `blocked` (Send/Enter pressed while the composer refused it) and
 * `network` (the stream request threw before any response).
 */
export type SendDropReason =
  | 'busy'
  | 'blocked'
  | 'no_conversation'
  | 'network'
  | 'steer_no_conversation'
  | 'steer_failed';

export type SendOutcome = { sent: true } | { sent: false; reason: SendDropReason | 'empty' | 'locked' };

export const SENT: SendOutcome = { sent: true };

/**
 * Drops whose optimistic bubble is gone (or never existed) and whose text the user has no other copy
 * of — the composer restores the draft for these. `network` keeps its bubble and shows the stream error,
 * and `blocked` never cleared the field in the first place.
 */
const RESTORABLE: ReadonlySet<string> = new Set<SendDropReason>(['busy', 'no_conversation', 'steer_no_conversation', 'steer_failed']);

export function shouldRestoreDraft(outcome: SendOutcome): boolean {
  return !outcome.sent && RESTORABLE.has(outcome.reason);
}

/**
 * Put a dropped send's text back — only into an EMPTY composer still showing the conversation it was
 * typed in. Anything the reader has typed since wins, and a draft never follows them into another thread
 * (the same rule `clearsDraftOnConversationChange` enforces). Shared 1:1 with iOS/Android `SendDropRestore`.
 */
export function restoredDraftText(
  draft: { conversationId: string | null; text: string },
  shown: { conversationId: string | null; input: string },
): string | null {
  if (!draft.text.trim()) return null;
  if (shown.input.trim()) return null;
  if ((draft.conversationId ?? '') !== (shown.conversationId ?? '')) return null;
  return draft.text;
}

export function reportSendDropped(
  reason: SendDropReason,
  details: { conversationId: string | null; textLength: number; detail?: string },
): void {
  const conversationId = details.conversationId ?? '';
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('send_drop_reason', reason);
    scope.setTag('conversation_id', conversationId);
    scope.setTag('text_len', String(details.textLength));
    if (details.detail) scope.setExtra('detail', details.detail);
    Sentry.captureMessage(`chat send dropped: ${reason}`);
  });
  logPageClickBi({
    page_name: 'chat',
    button_name: 'send_message_dropped',
    state: reason,
    action: `text_len:${details.textLength}${details.detail ? ` ${details.detail.slice(0, 150)}` : ''}`,
    ...(conversationId ? { conversation_id: conversationId } : {}),
  });
}
